import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { Department } from '../../types/asset.types';
import { ApiEndpoints } from '../../services/ApiUrls';

export interface DepartmentPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Department[];
}

export interface DepartmentCreateInput {
  company: string;
  department_code: string;
  name: string;
  description?: string;
}

export interface DepartmentUpdateInput extends Partial<DepartmentCreateInput> {
  updateReason?: string;
}

export interface DepartmentFilter {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: string;
  dropdown?: boolean;
}

export const departmentApi = createApi({
  reducerPath: 'departmentApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Department', 'DepartmentHistory'],
  endpoints: (builder) => ({
    getDepartments: builder.query<DepartmentPaginatedResponse, DepartmentFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter) {
          if (filter.page) params.append('page', filter.page.toString());
          if (filter.limit) params.append('limit', filter.limit.toString());
          if (filter.search) params.append('search', filter.search);
          if (filter.companyId) params.append('company', filter.companyId);
          if (filter.dropdown !== undefined) params.append('dropdown', filter.dropdown.toString());
        }
        return { url: ApiEndpoints.departments.list, params };
      },
      providesTags: ['Department'],
    }),

    // New dropdown-specific endpoint
    getDepartmentsDropdown: builder.query<DepartmentPaginatedResponse, DepartmentFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter) {
          if (filter.page) params.append('page', filter.page.toString());
          if (filter.limit) params.append('limit', filter.limit.toString());
          if (filter.search) params.append('search', filter.search);
        }
        return { url: 'company/departments/dropdown/', params };
      },
      providesTags: ['Department'],
    }),

    getDepartmentById: builder.query<Department, string>({
      query: (id) => ApiEndpoints.departments.detail(id),
      providesTags: (result, error, id) => [{ type: 'Department', id }],
    }),

    createDepartment: builder.mutation<Department, DepartmentCreateInput>({
      query: (department) => ({
        url: ApiEndpoints.departments.list,
        method: 'POST',
        body: department,
      }),
      invalidatesTags: ['Department'],
    }),

    updateDepartment: builder.mutation<Department, { id: string; department: DepartmentUpdateInput }>({
      query: ({ id, department }) => ({
        url: ApiEndpoints.departments.detail(id),
        method: 'PATCH',
        body: department,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Department', id },
        'Department',
      ],
    }),

    getDepartmentHistory: builder.query<DepartmentHistoryData, string>({
      query: (id) => ApiEndpoints.departments.history(id),
      providesTags: (result, error, id) => [{ type: 'DepartmentHistory', id }],
    }),
  }),
});

// Export hooks
export const {
  useGetDepartmentsQuery,
  useGetDepartmentsDropdownQuery,
  useGetDepartmentByIdQuery,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useGetDepartmentHistoryQuery,
} = departmentApi;
